import { apiClient } from '../api/apiClient';
import { appConfig } from '../config/appConfig';
import type { Letter, LetterEdit } from '../models/Letter';
import type { Memory } from '../models/Memory';
import type {
  AdminLetter,
  AdminMemory,
  AdminPhoto,
  IssuedInvite,
  MemoryEdit,
  ModerationQueue,
} from '../models/Moderation';
import type { SiteConfig } from '../models/SiteConfig';
import { delay } from '../utils/delay';
import { preparePhoto } from '../utils/preparePhoto';
import { writeMockMainPhoto, writeMockSiteConfig } from './mockSiteConfig';

export interface AdminService {
  getQueue(): Promise<ModerationQueue>;
  approveMemory(id: string): Promise<void>;
  removeMemory(id: string): Promise<void>;
  approvePhoto(id: string): Promise<void>;
  removePhoto(id: string): Promise<void>;
  saveSiteConfig(config: SiteConfig): Promise<SiteConfig>;
  /** Replaces the memorial's main photograph. Returns where it can now be seen. */
  uploadMainPhoto(file: File): Promise<{ mainPhoto: string }>;
  removeMainPhoto(): Promise<void>;
  rotateInvite(): Promise<IssuedInvite>;
  listAllMemories(): Promise<AdminMemory[]>;
  listAllPhotos(): Promise<AdminPhoto[]>;
  updateMemory(id: string, edit: MemoryEdit): Promise<Memory>;
  /* Letters, moderated exactly as memories are. */
  approveLetter(id: string): Promise<void>;
  removeLetter(id: string): Promise<void>;
  listAllLetters(): Promise<AdminLetter[]>;
  updateLetter(id: string, edit: LetterEdit): Promise<Letter>;
}

/**
 * Used while no backend is configured, so the administration screens can be
 * built and looked at without one. Nothing here persists.
 */
export function createMockAdminService(): AdminService {
  let queue: ModerationQueue = {
    memories: [
      {
        id: 'demo-pending-1',
        name: 'Demo Contributor',
        message: 'An example submission, shown so that the moderation queue can be reviewed.',
        created: '2026-08-18T09:30:00.000Z',
      },
    ],
    letters: [
      {
        id: 'demo-pending-letter-1',
        name: 'Demo Contributor',
        message: 'An example letter waiting to be approved, so the queue can be reviewed.',
        created: '2026-08-19T11:00:00.000Z',
      },
    ],
    photos: [],
  };

  // The mock also shows what the "on the site" lists look like, so an
  // administrator can try editing and deleting without a backend. These start
  // with one published item of each kind so the lists are never empty.
  let allMemories: AdminMemory[] = [
    {
      id: 'demo-published-1',
      name: 'A Family Member',
      message: 'A memory that has already been published, so it can be edited or taken down.',
      created: '2026-08-10T14:00:00.000Z',
      status: 'published',
    },
  ];

  let allLetters: AdminLetter[] = [
    {
      id: 'demo-published-letter-1',
      name: 'A Family Member',
      message:
        'Dear Dad,\n\nA letter that has already been published, so it can be edited or taken down.',
      created: '2026-08-11T09:00:00.000Z',
      status: 'published',
    },
  ];

  let allPhotos: AdminPhoto[] = [
    {
      id: 'demo-photo-1',
      url: '/photos/demo-photo-1.jpg',
      thumbnailUrl: '/photos/demo-photo-1-thumb.jpg',
      status: 'published',
      caption: 'A photograph already on the site.',
      uploaded: '2026-08-10T14:05:00.000Z',
    },
  ];

  return {
    async getQueue() {
      await delay(200);
      return {
        memories: [...queue.memories],
        letters: [...queue.letters],
        photos: [...queue.photos],
      };
    },
    async approveMemory(id) {
      await delay(200);
      queue = { ...queue, memories: queue.memories.filter((memory) => memory.id !== id) };
    },
    async removeMemory(id) {
      await delay(200);
      queue = { ...queue, memories: queue.memories.filter((memory) => memory.id !== id) };
      // A memory may already be on the site rather than waiting, so the
      // published list is kept in step too.
      allMemories = allMemories.filter((memory) => memory.id !== id);
    },
    async approvePhoto(id) {
      await delay(200);
      queue = { ...queue, photos: queue.photos.filter((photo) => photo.id !== id) };
    },
    async removePhoto(id) {
      await delay(200);
      queue = { ...queue, photos: queue.photos.filter((photo) => photo.id !== id) };
      allPhotos = allPhotos.filter((photo) => photo.id !== id);
    },
    async approveLetter(id) {
      await delay(200);
      queue = { ...queue, letters: queue.letters.filter((letter) => letter.id !== id) };
      allLetters = allLetters.map((letter) =>
        letter.id === id ? { ...letter, status: 'published' } : letter,
      );
    },
    async removeLetter(id) {
      await delay(200);
      queue = { ...queue, letters: queue.letters.filter((letter) => letter.id !== id) };
      allLetters = allLetters.filter((letter) => letter.id !== id);
    },
    async listAllLetters() {
      await delay(200);
      return [...allLetters];
    },
    async updateLetter(id, edit) {
      await delay(200);
      const existing = allLetters.find((letter) => letter.id === id);
      // The timestamp and the status are carried over, matching the Worker.
      const updated: AdminLetter = {
        id,
        name: edit.name,
        message: edit.message,
        created: existing?.created ?? new Date().toISOString(),
        status: existing?.status ?? 'pending',
      };
      allLetters = allLetters.map((letter) => (letter.id === id ? updated : letter));
      const { status: _status, ...publicLetter } = updated;
      return publicLetter;
    },
    async saveSiteConfig(config) {
      await delay(300);
      // Kept for the rest of the session so the home page shows the change, the
      // way it would with a Worker behind it. See services/mockSiteConfig.ts.
      return writeMockSiteConfig(config);
    },
    async uploadMainPhoto(file) {
      await delay(300);
      // Nothing leaves the browser, so the chosen file is shown back as it is.
      // The site's own policy allows blob: images, which is what makes the
      // preview render.
      const mainPhoto = URL.createObjectURL(file);
      writeMockMainPhoto(mainPhoto);
      return { mainPhoto };
    },
    async removeMainPhoto() {
      await delay(200);
      writeMockMainPhoto('');
    },
    async rotateInvite() {
      await delay(300);
      return { token: 'example-invitation-token-not-a-real-one', version: 1 };
    },
    async listAllMemories() {
      await delay(200);
      return [...allMemories];
    },
    async listAllPhotos() {
      await delay(200);
      return [...allPhotos];
    },
    async updateMemory(id, edit) {
      await delay(200);
      const updated: Memory = {
        id,
        name: edit.name,
        message: edit.message,
        created:
          allMemories.find((memory) => memory.id === id)?.created ?? new Date().toISOString(),
      };
      allMemories = allMemories.map((memory) =>
        memory.id === id ? { ...memory, ...updated } : memory,
      );
      return updated;
    },
  };
}

export function createHttpAdminService(): AdminService {
  return {
    getQueue() {
      return apiClient.get<ModerationQueue>('/api/admin/queue');
    },
    async approveMemory(id) {
      await apiClient.post(`/api/admin/memories/${encodeURIComponent(id)}/approve`, {});
    },
    async removeMemory(id) {
      await apiClient.post(`/api/admin/memories/${encodeURIComponent(id)}/remove`, {});
    },
    async approvePhoto(id) {
      await apiClient.post(`/api/admin/photos/${encodeURIComponent(id)}/approve`, {});
    },
    async removePhoto(id) {
      await apiClient.post(`/api/admin/photos/${encodeURIComponent(id)}/remove`, {});
    },
    async approveLetter(id) {
      await apiClient.post(`/api/admin/letters/${encodeURIComponent(id)}/approve`, {});
    },
    async removeLetter(id) {
      await apiClient.post(`/api/admin/letters/${encodeURIComponent(id)}/remove`, {});
    },
    async listAllLetters() {
      const { letters } = await apiClient.get<{ letters: AdminLetter[] }>('/api/admin/letters');
      return letters;
    },
    updateLetter(id, edit) {
      return apiClient.put<Letter>(`/api/admin/letters/${encodeURIComponent(id)}`, edit);
    },
    saveSiteConfig(config) {
      return apiClient.put<SiteConfig>('/api/config', config);
    },
    /**
     * Downscales and re-encodes before sending, exactly as a contributor's
     * upload does: it keeps the Worker inside its CPU budget, and re-encoding is
     * what strips the EXIF block -- including any GPS coordinates -- before the
     * photograph leaves the device.
     *
     * Only the full-size image is sent. preparePhoto also produces a thumbnail,
     * which has no use for a picture shown at the top of the home page.
     */
    async uploadMainPhoto(file) {
      const prepared = await preparePhoto(file);

      const formData = new FormData();
      formData.append('photo', prepared.image, 'main-photo.jpg');

      return apiClient.put<{ mainPhoto: string }>('/api/admin/main-photo', formData);
    },
    async removeMainPhoto() {
      await apiClient.post('/api/admin/main-photo/remove', {});
    },
    rotateInvite() {
      return apiClient.post<IssuedInvite>('/api/admin/invite/rotate', {});
    },
    async listAllMemories() {
      const result = await apiClient.get<{ memories: AdminMemory[] }>('/api/admin/memories');
      return result.memories;
    },
    async listAllPhotos() {
      const result = await apiClient.get<{ photos: AdminPhoto[] }>('/api/admin/photos');
      return result.photos;
    },
    updateMemory(id, edit) {
      return apiClient.put<Memory>(`/api/admin/memories/${encodeURIComponent(id)}`, edit);
    },
  };
}

export const adminService: AdminService = appConfig.useMockData
  ? createMockAdminService()
  : createHttpAdminService();
